/**
 * SieAccountingBasisCard Component
 * 
 * Clickable module card for "Redovisningsgrund (utifrån SIE-fil)" in CaseDetailPage.
 * Clicking navigates to the detail page with tabs for SIE analysis.
 * 
 * Uses the shared useSieMasterDataStatus hook for automatic synchronization
 * when SIE data is updated in other modules.
 */

import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { FileSpreadsheet, CheckCircle, Clock, Loader2 } from 'lucide-react';
import { useSieMasterDataStatus } from '@/hooks/useSieMasterData';

interface SieAccountingBasisCardProps {
  auditId: string;
  className?: string;
}

export function SieAccountingBasisCard({ auditId, className = '' }: SieAccountingBasisCardProps) {
  const navigate = useNavigate();
  
  // Use shared hook for automatic synchronization across modules
  const { hasData, companyName, isLoading } = useSieMasterDataStatus(auditId);

  const handleClick = () => {
    navigate(`/cases/${auditId}/sie-accounting-basis`);
  };

  const status = hasData ? 'completed' : 'not_started';
  const statusLabel = hasData ? 'Bearbetad' : 'Ej uppladdad';
  const statusColor = hasData 
    ? 'bg-success/10 text-success border-success/20' 
    : 'bg-muted text-muted-foreground border-muted';

  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md hover:border-primary/50 ${className}`}
      onClick={handleClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4" />
              )}
            </div>
            <div>
              <h3 className="font-medium text-sm">Redovisningsgrund (utifrån SIE-fil)</h3>
              <p className="text-xs text-muted-foreground">
                {hasData && companyName 
                  ? companyName 
                  : 'Ladda upp SIE-fil för att extrahera bokföringsdata'
                }
              </p>
            </div>
          </div>
          <Badge className={statusColor} variant="outline">
            {hasData ? (
              <>
                <CheckCircle className="h-3 w-3 mr-1" />
                {statusLabel}
              </>
            ) : (
              <>
                <Clock className="h-3 w-3 mr-1" />
                {statusLabel}
              </>
            )}
          </Badge>
        </div>
        
        {hasData && (
          <Progress value={100} className="h-1 mt-2" />
        )}
      </CardContent>
    </Card>
  );
}
